import React, { useState } from 'react';
import { Check, Tag } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const EditorialPricingSection: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const { theme } = useTheme();

  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#FCFCF9';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const badgeBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderGradient = theme === 'dark' 
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';
  const badgeBorderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';

  const plans = [
    {
      name: "Minimums",
      subtitle: "The essential infrastructure",
      monthlyPrice: 197,
      annualPrice: 1970,
      description: "Marketing Platform Foundation",
      features: [
        "Unlimited User Seats",
        "3 Client Sub-Accounts",
        "$10 complimentary credits each month (approx. 10,000 emails or 770 texts)",
        "Website & Funnel Builder",
        "CRM & Pipelines",
        "Email + SMS Marketing",
        "Unified Conversations",
        "Social Media Management",
        "Workflow Automation",
        "Forms, Surveys & Quizzes",
        "Calendar & Bookings",
        "Ad Manager",
        "Payments & Invoicing",
        "Web Chat Widget",
        "Missed Call Text Back",
        "24/7 Support",
        "Basic API Access",
        "1:1 Onboarding",
        "10 weeks of Group Training",
        "Demo Video Library"
      ],
      monthlyUrl: "https://buy.stripe.com/5kQ14o3HV0yO332bjta3u09",
      annualUrl: "https://buy.stripe.com/cNiaEYfqDdlA7jibjta3u0a",
      popular: false
    },
    {
      name: "Professional",
      subtitle: "The professional business benchmark",
      monthlyPrice: 297,
      annualPrice: 2970,
      description: "Advanced Automation & Multi-Channel Growth",
      features: [
        "All Minimums features",
        "Native AI Tool for Text Conversations, Phone Calls, Content, Web page Building and Review Management",
        "Booking Calendars",
        "Online Reputation Management",
        "Communities",
        "Courses",
        "Grey Labeled Mobile App",
        "Google Business Profile Optimisation",
        "Google Business Profile Call Tracking",
        "Facebook Messenger integration",
        "Text to Pay",
        "Unlimited User Seats",
        "Unlimited Client Sub-Accounts",
        "$25 complimentary credits each month (approx. 25,000 emails)",
        "Advanced Workflow Automation",
        "Advanced Triggers & Webhooks",
        "Custom Integrations (Zapier, Make, API)",
        "Advanced Email & SMS Campaigns",
        "A/B Testing",
        "Advanced Reporting & Analytics",
        "Multi-channel Attribution",
        "Campaign Performance Tracking",
        "Lead Scoring & Segmentation",
        "Custom Fields & Tags",
        "Bulk Actions & Imports",
        "DFY A2P registration",
        "Priority Support & Unlimited Zoom",
        "Dedicated Success Manager"
      ],
      monthlyUrl: "https://buy.stripe.com/eVqcN62DRgxMbzy2MXa3u0b",
      annualUrl: "https://buy.stripe.com/5kQ9AU3HV0yO6fe1ITa3u0c",
      popular: true
    },
    {
      name: "Enterprise",
      subtitle: "The elite, AI-driven executive suite",
      monthlyPrice: 697,
      annualPrice: 6970,
      description: "Enterprise-Grade Platform with SaaS Mode & Advanced AI",
      features: [
        "All Minimums & Professional features",
        "Unlimited User Seats",
        "Unlimited Client Sub-Accounts",
        "$100 complimentary credits each month (approx. 100,000 emails)",
        "Unused credits rollover",
        "SaaS Mode (White-label & Resell Platform)",
        "Rebill Phone & Email with Markup",
        "Advanced API Access",
        "Automated Account Reporting",
        "Advanced AI Workflow Builder",
        "Advanced AI Funnel Builder",
        "Advanced AI Website Builder",
        "AI Content Generation (Blogs, Social, Emails)",
        "E-Signing, Documents & Contracts",
        "Advanced Invoicing & Payments (Stripe integration)",
        "Advanced Memberships & Courses Platform",
        "Advanced Social Planner with AI Scheduling",
        "Advanced Reporting (Custom Dashboards, Attribution)",
        "AI Chat with Custom Training",
        "AI Convo (Voice AI)",
        "Outbound Sales AI & Dialer",
        "Advanced AI Access & Priority Processing",
        "Custom AI Knowledge Base Setup",
        "Custom Branding & White-labeling",
        "20+ Premium Funnel Templates",
        "70+ Premium Snapshots",
        "Private Slack Channel",
        "Dedicated Account Manager",
        "Lifetime Pricing Guarantee"
      ],
      monthlyUrl: "https://buy.stripe.com/5kQ9AU3HV0yO6fe1ITa3u0c",
      annualUrl: "https://buy.stripe.com/aFa7sM7Yb0yO9rqfzJa3u0g",
      popular: false
    }
  ];

  const benefits = [
    "14-day free trial",
    "No setup fees",
    "Cancel anytime",
    "Free CRM Migration",
    "1:1 Onboarding"
  ];

  return (
    <section id="pricing-section" className="py-16 transition-colors duration-300">
      <div className="max-w-[960px] mx-auto px-[7%] sm:px-6 lg:px-8">
        {/* Pricing Badge */}
        <div className="flex justify-center pt-2 pb-6">
          <div className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full overflow-hidden">
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: badgeBorderGradient,
                backgroundSize: '200% 100%',
                animation: 'borderTrace 2s linear infinite',
              }}
            />
            <div 
              className="absolute inset-0 rounded-full"
              style={{ border: `1px solid ${badgeBorderColor}` }}
            />
            <div 
              className="absolute inset-[1px] rounded-full"
              style={{ backgroundColor: badgeBgColor }}
            />
            <Tag className="relative z-10 w-3 h-3" style={{ color: textColor }} />
            <span className="relative z-10 text-xs font-medium" style={{ color: headingColor }}>
              Pricing
            </span>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight" style={{ color: headingColor }}>
            Scale Your Business. Not Your Costs.
          </h2>
          <p className="text-sm" style={{ color: textColor }}>
            Chose the plan that suits. Start for free.
          </p>
        </div>

        {/* Toggle with Save Badge */}
        <div className="flex flex-col items-center mb-6">
          <div 
            className="inline-flex items-center rounded-full p-1"
            style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
          >
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200`}
              style={{
                backgroundColor: !isAnnual ? (theme === 'dark' ? '#FFFFFF' : '#111111') : 'transparent',
                color: !isAnnual ? (theme === 'dark' ? '#000000' : '#FFFFFF') : textColor
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200`}
              style={{
                backgroundColor: isAnnual ? (theme === 'dark' ? '#FFFFFF' : '#111111') : 'transparent',
                color: isAnnual ? (theme === 'dark' ? '#000000' : '#FFFFFF') : textColor
              }}
            >
              Annual
            </button>
          </div>
          {/* Save Badge - below toggle */}
          <div className="relative inline-flex items-center px-2.5 py-1 rounded-full overflow-hidden mt-3">
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: badgeBorderGradient,
                backgroundSize: '200% 100%',
                animation: 'borderTrace 2s linear infinite',
              }}
            />
            <div 
              className="absolute inset-0 rounded-full"
              style={{ border: `1px solid ${badgeBorderColor}` }}
            />
            <div 
              className="absolute inset-[1px] rounded-full"
              style={{ backgroundColor: badgeBgColor }}
            />
            <span className="relative z-10 text-[10px] font-medium" style={{ color: headingColor }}>
              Save up to 17%
            </span>
          </div>
        </div>


        {/* Benefits */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-1.5 text-xs" style={{ color: textColor }}>
              <Check className="w-3 h-3" style={{ color: headingColor }} />
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan, index) => {
            const displayPrice = isAnnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;
            const url = isAnnual ? plan.annualUrl : plan.monthlyUrl;
            
            return (
              <div
                key={index}
                className={`relative rounded-2xl border overflow-hidden transition-all duration-200`}
                style={{
                  backgroundColor: cardBgColor,
                  borderColor: plan.popular ? (theme === 'dark' ? '#FFFFFF' : '#111111') : borderColor,
                  transform: 'translateY(-4px)',
                  boxShadow: plan.popular 
                    ? '0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.15)' 
                    : '0 20px 40px -10px rgba(0, 0, 0, 0.1), 0 10px 20px -5px rgba(0, 0, 0, 0.05)'
                }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div 
                    className="absolute top-0 left-0 right-0 text-center py-1.5"
                    style={{ 
                      backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111',
                      color: theme === 'dark' ? '#000000' : '#FFFFFF'
                    }}
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Most Popular</span>
                  </div>
                )}

                <div className={`p-5 ${plan.popular ? 'pt-12' : ''}`}>
                  {/* Plan Name & Price */}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold mb-1" style={{ color: headingColor }}>{plan.name}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold" style={{ color: headingColor }}>${displayPrice}</span>
                      <span className="text-xs" style={{ color: textColor }}>/month</span>
                    </div>
                    {isAnnual && (
                      <p className="text-[10px] mt-1" style={{ color: textColor }}>
                        Billed ${plan.annualPrice}/year
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <p 
                    className="text-xs mb-4 pb-4"
                    style={{ color: textColor, borderBottom: `1px solid ${borderColor}` }}
                  >
                    {plan.description}
                  </p>

                  {/* Features */}
                  <div className="mb-4">
                    <ul className="space-y-2">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <div 
                            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}
                          >
                            <Check 
                              className="w-2.5 h-2.5" 
                              strokeWidth={3} 
                              style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}
                            />
                          </div>
                          <span className="text-xs leading-relaxed" style={{ color: textColor }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-2 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                    style={{
                      backgroundColor: plan.popular 
                        ? (theme === 'dark' ? '#FFFFFF' : '#111111')
                        : (theme === 'dark' ? '#222222' : '#FCFCF9'),
                      color: plan.popular 
                        ? (theme === 'dark' ? '#000000' : '#FFFFFF')
                        : headingColor,
                      border: plan.popular ? 'none' : `1px solid ${borderColor}`
                    }}
                  >
                    Start Free Trial
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EditorialPricingSection;
